import { getInsurancesByPriority } from "@/app/directories/companion/(main)/roster/helper";
import type { PhotoMetaData } from "@/app/directories/companion/components_v2/PatientHeader/UI/ManagePhotosModal";
import { datadogLogs } from "@datadog/browser-logs";
import {
  downloadConsumerPhotoFromPresignedUrl,
  getAllEnabledOrgConfigFlagsByOrgId,
  getConsumerPhotoDownloadUrl,
  getConsumerPhotoUploadUrl,
  getConsumerPhotos,
  markConsumerPhotoAsDeleted,
  uploadConsumerPhotoToPresignedUrl,
  writeConsumerPhotoToDB,
} from "@frontend/lib/actions/actions";
import { PhotoType } from "@frontend/lib/graphql/generated-gql";
import type { InsuranceData } from "@frontend/lib/schemas/clientSchema";
import { v4 as uuidv4 } from "uuid";
import { isPhotoExpired } from "./photo-expiry";

// Extended PhotoMetaData type that includes ocrMetadata (available after GraphQL regeneration)
type PhotoMetaDataWithOcr = PhotoMetaData & {
  ocrMetadata?: Record<string, unknown> | null;
};

/**
 * Check if an existing photo of the same type has a valid (non-expired) expiry date
 * Used to prevent duplicate photo uploads when existing photo is still valid
 */
export const hasValidExistingPhoto = (
  existingPhotos: PhotoMetaData[],
  photoType: PhotoType,
  linkedInsuranceId?: string | null,
): boolean => {
  // Find existing photos of the same type
  const matchingPhotos = existingPhotos.filter((photo) => {
    // Match by photo type
    if (photo.type !== photoType) return false;

    // For insurance photos, also match by linkedInsuranceId
    if (
      photoType === PhotoType.InsuranceCardFront ||
      photoType === PhotoType.InsuranceCardBack
    ) {
      return photo.linkedInsuranceId === linkedInsuranceId;
    }

    return true;
  });

  // Check if any matching photo has a valid (non-expired) expiry date
  return matchingPhotos.some((photo) => {
    const photoWithOcr = photo as PhotoMetaDataWithOcr;
    return !isPhotoExpired(photoWithOcr.ocrMetadata);
  });
};
export type PhotoPrompt = {
  type: PhotoType;
  currentPhoto: PhotoMetaData | null;
  associatedPhotoId: string;
  insurancePriority?: number;
  linkToInsuranceId?: string;
};

const getInsurancePhotoPrompts = (
  consumerPhotos: PhotoMetaData[],
  insurance: InsuranceData,
  priority: number,
) => {
  const front = consumerPhotos.find(
    (photo) =>
      photo.type === PhotoType.InsuranceCardFront &&
      photo.linkedInsuranceId === insurance.id,
  );
  const back = consumerPhotos.find(
    (photo) =>
      photo.type === PhotoType.InsuranceCardBack &&
      photo.linkedInsuranceId === insurance.id,
  );

  const associatedPhotoId =
    front?.associatedPhotoId || back?.associatedPhotoId || uuidv4();

  return {
    front: {
      type: PhotoType.InsuranceCardFront,
      currentPhoto: front ? (front as PhotoMetaData) : null,
      associatedPhotoId,
      insurancePriority: insurance.priority || priority,
      linkToInsuranceId: insurance.id,
    },
    back: {
      type: PhotoType.InsuranceCardBack,
      currentPhoto: back ? (back as PhotoMetaData) : null,
      associatedPhotoId,
      insurancePriority: insurance.priority || priority,
      linkToInsuranceId: insurance.id,
    },
  };
};

/**
 * This is a mock function that returns a list of photos to prompt from the consumer.
 * This will have to handle the logic of which photos need to be uploaded based on org config
 * and the consumer's current uploaded photos (if any) as well as insurances and their statuses.
 *
 * currently it just prompts for data if it doesn't exist. that is to say, if the consumer has no id card,
 * it will prompt for the id card front and back. if the consumer has an id card, it will not prompt for it.
 * similarly, if the consumer has no uploaded insurance cards, it will prompt for the front and back of the insurance card.
 * BUT if the consumer doesn't have insurance with us, we won't prompt for an insurance card since we can't update
 * our system yet from account or via OCR.
 */
export const getConsumerPhotosToPrompt = async (
  consumerId: string,
  orgId: number,
  insurances?: InsuranceData[],
) => {
  const configFlags = await getAllEnabledOrgConfigFlagsByOrgId(orgId);
  if (configFlags && !configFlags.prompt_photos_intake) {
    return [];
  }

  const consumerPhotos = await getConsumerPhotos(consumerId, orgId);
  if (!consumerPhotos) {
    return [];
  }
  // possibly handle required here
  const photoPrompts: PhotoPrompt[] = [];

  // id photo prompts
  const idFront = consumerPhotos.find(
    (photo) => photo.type === PhotoType.IdCardFront,
  );
  const idBack = consumerPhotos.find(
    (photo) => photo.type === PhotoType.IdCardBack,
  );

  const identificationAssociatedPhotoId =
    idFront?.associatedPhotoId || idBack?.associatedPhotoId || uuidv4();

  photoPrompts.push({
    type: PhotoType.IdCardFront,
    currentPhoto: idFront ? (idFront as PhotoMetaData) : null,
    associatedPhotoId: identificationAssociatedPhotoId,
  });

  photoPrompts.push({
    type: PhotoType.IdCardBack,
    currentPhoto: idBack ? (idBack as PhotoMetaData) : null,
    associatedPhotoId: identificationAssociatedPhotoId,
  });

  // insurance photo prompts
  if (insurances && insurances.length > 0) {
    const [primary, secondary] = getInsurancesByPriority(insurances);

    if (primary) {
      const { front, back } = getInsurancePhotoPrompts(
        consumerPhotos,
        primary,
        1,
      );
      photoPrompts.push(front);
      photoPrompts.push(back);
    }

    if (secondary) {
      const { front, back } = getInsurancePhotoPrompts(
        consumerPhotos,
        secondary,
        2,
      );
      photoPrompts.push(front);
      photoPrompts.push(back);
    }
  }

  return photoPrompts;
};

export const uploadConsumerPhoto = async (
  photoFileToUpload: File,
  consumerId: string,
  orgId: number,
  photoType: PhotoType,
  isIntake: boolean,
  associatedPhotoId: string,
  uuid?: string,
  linkedInsuranceId?: string | null,
  ocrMetadata?: Record<string, unknown> | null,
) => {
  datadogLogs.logger.info(
    `[uploadConsumerPhoto] Uploading consumer photo for consumerId: ${consumerId}`,
    {
      consumerId,
      orgId,
      photoType,
      isIntake,
      associatedPhotoId,
      uuid,
      linkedInsuranceId,
    },
  );
  const uploadUrl = await getConsumerPhotoUploadUrl(
    consumerId,
    orgId,
    photoFileToUpload.type,
    true,
    uuid,
  );
  if (!uploadUrl) {
    datadogLogs.logger.error(
      `[uploadConsumerPhoto] Failed to get upload URL for consumerId: ${consumerId}`,
      {
        consumerId,
        orgId,
        photoType,
      },
    );
    return;
  }

  const uploadSuccess = await uploadConsumerPhotoToPresignedUrl(
    photoFileToUpload,
    uploadUrl.url,
    consumerId,
    orgId,
    true,
  );
  if (!uploadSuccess) {
    datadogLogs.logger.error(
      `[uploadConsumerPhoto] Failed to upload photo to presigned URL for consumerId: ${consumerId}`,
      { consumerId, orgId, photoType },
    );
    return;
  }

  const writeResponse:
    | {
        status: number;
        responseStatus: string;
        data: {
          message: string;
        };
      }
    | undefined = await writeConsumerPhotoToDB(
    uploadUrl.photoId,
    consumerId,
    orgId,
    photoType,
    isIntake,
    associatedPhotoId,
    linkedInsuranceId,
    ocrMetadata,
  );
  if (!writeResponse || writeResponse.status !== 200) {
    datadogLogs.logger.error(
      `[uploadConsumerPhoto] Failed to write photo to DB for consumerId: ${consumerId}`,
      {
        consumerId,
        orgId,
        photoType,
      },
    );
    return;
  }
  datadogLogs.logger.info(
    `[uploadConsumerPhoto] Successfully uploaded photo for consumerId: ${consumerId}`,
    {
      consumerId,
      orgId,
      photoType,
      isIntake,
      associatedPhotoId,
      linkedInsuranceId,
    },
  );
  return writeResponse;
};

export const downloadConsumerPhoto = async (
  photoId: string,
  consumerId: string,
  orgId: number,
) => {
  const downloadUrl = await getConsumerPhotoDownloadUrl(
    photoId,
    consumerId,
    orgId,
    true,
  );
  if (!downloadUrl) {
    datadogLogs.logger.error("Failed to get download URL for consumerId: ", {
      consumerId,
      orgId,
      photoId,
    });
    return;
  }
  const photoFileResponse = await downloadConsumerPhotoFromPresignedUrl(
    downloadUrl,
    true,
  );
  if (!photoFileResponse) {
    datadogLogs.logger.error(
      "Failed to download photo from presigned URL for consumerId: ",
      { consumerId, orgId, photoId },
    );
    return;
  }

  const blobUrl = URL.createObjectURL(photoFileResponse);

  return { response: photoFileResponse, photoId: photoId, blobUrl: blobUrl };
};

export const deleteConsumerPhoto = async (
  photoId: string,
  consumerId: string,
  orgId: number,
) => {
  const deleteResponse:
    | {
        updateOneConsumerPhotoMetaData: {
          id: string;
          status: string;
        };
      }
    | undefined = await markConsumerPhotoAsDeleted(photoId, consumerId, orgId);
  if (
    !deleteResponse ||
    !deleteResponse.updateOneConsumerPhotoMetaData ||
    deleteResponse.updateOneConsumerPhotoMetaData.status !== "DELETED"
  ) {
    datadogLogs.logger.error(
      "Failed to delete photo from DB for consumerId: ",
      { consumerId, orgId, photoId },
    );
    return;
  }
  return deleteResponse;
};
